import React, { useEffect, useRef, useState, useCallback } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Card } from './ui/card';
import { Navigation, Clock, MapPin } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Helper: parse duration from various formats the Routes API may return
const parseDurationSeconds = (d) => {
  if (typeof d === 'number') return d;
  if (typeof d === 'string') { const m = d.match(/(\d+)/); return m ? parseInt(m[1]) : 0; }
  if (d?.value != null) return d.value;
  return 0;
};

// Route Display — tries new Route.computeRoutes, falls back to legacy DirectionsService
const RouteDisplay = ({ pickup, dropoff, onRouteReady }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');
  const polylinesRef = useRef([]);

  useEffect(() => {
    if (!map || !routesLibrary || !pickup?.lat || !pickup?.lng || !dropoff?.lat || !dropoff?.lng) return;

    let active = true;
    const origin = { lat: pickup.lat, lng: pickup.lng };
    const dest = { lat: dropoff.lat, lng: dropoff.lng };

    const cleanup = () => {
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current = [];
    };

    const applyResult = (path, distKm, durMin, steps) => {
      if (!active) return;
      cleanup();
      // Polyline from path
      const polyline = new google.maps.Polyline({
        path, strokeColor: '#3b82f6', strokeWeight: 6, strokeOpacity: 1, map
      });
      polylinesRef.current = [polyline];
      // Fit bounds
      const bounds = new google.maps.LatLngBounds();
      path.forEach(p => bounds.extend(p));
      map.fitBounds(bounds, { padding: { top: 100, bottom: 100, left: 50, right: 50 } });
      if (onRouteReady) onRouteReady({ distance: distKm, duration: durMin, steps });
    };

    const computeRoute = async () => {
      // Try new Route API
      if (routesLibrary.Route) {
        try {
          const { routes } = await routesLibrary.Route.computeRoutes({
            origin, destination: dest, travelMode: 'DRIVING', fields: ['legs', 'path'],
          });
          if (routes?.length) {
            const leg = routes[0].legs?.[0];
            const distKm = ((leg?.distanceMeters || 0) / 1000).toFixed(1);
            const durMin = Math.round(parseDurationSeconds(leg?.duration) / 60);
            const steps = leg?.steps?.map(s => ({
              instruction: (s.instructions || '').replace(/<[^>]*>/g, ''),
              distance: s.localizedValues?.distance || '',
              maneuver: s.maneuver || ''
            })) || [];
            applyResult(routes[0].path || [], distKm, durMin, steps);
            return;
          }
        } catch (err) {
          console.warn('Routes API unavailable, using legacy Directions API:', err.message);
        }
      }

      // Fallback: legacy DirectionsService
      const service = new routesLibrary.DirectionsService();
      service.route({ origin, destination: dest, travelMode: google.maps.TravelMode.DRIVING }, (result, status) => {
        if (!active || status !== google.maps.DirectionsStatus.OK || !result) return;
        const route = result.routes[0];
        const leg = route.legs[0];
        applyResult(
          route.overview_path,
          (leg.distance?.value / 1000).toFixed(1),
          Math.round(leg.duration?.value / 60),
          leg.steps?.map(s => ({
            instruction: (s.instructions || '').replace(/<[^>]*>/g, ''),
            distance: s.distance?.text || '',
            maneuver: s.maneuver || ''
          })) || []
        );
      });
    };

    computeRoute();
    return () => { active = false; cleanup(); };
  }, [map, routesLibrary, pickup, dropoff, onRouteReady]);

  return null;
};

// Map Content Component
const MapContent = ({ pickup, dropoff, onRouteReady }) => {
  return (
    <>
      <RouteDisplay pickup={pickup} dropoff={dropoff} onRouteReady={onRouteReady} />
      
      {/* Pickup marker */}
      {pickup?.lat && pickup?.lng && (
        <AdvancedMarker position={{ lat: pickup.lat, lng: pickup.lng }}>
          <div className="relative">
            <div className="w-6 h-6 rounded-full border-2 border-white shadow-lg bg-green-500" />
          </div>
        </AdvancedMarker>
      )}
      
      {/* Dropoff marker */}
      {dropoff?.lat && dropoff?.lng && (
        <AdvancedMarker position={{ lat: dropoff.lat, lng: dropoff.lng }}>
          <div className="relative">
            <div className="w-6 h-6 rounded-full border-2 border-white shadow-lg bg-red-500" />
          </div>
        </AdvancedMarker>
      )}
    </>
  );
};

const NavigationMap = ({ pickup, dropoff, onRouteUpdate }) => {
  const [routeInfo, setRouteInfo] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleRouteReady = useCallback((info) => {
    setRouteInfo(info);
    if (info.steps?.length > 0) {
      setCurrentStep(info.steps[0]);
    }
    setIsLoading(false);
    if (onRouteUpdate) {
      onRouteUpdate(info);
    }
  }, [onRouteUpdate]);

  // Check for valid coordinates
  if (!pickup?.lng || !pickup?.lat || !dropoff?.lng || !dropoff?.lat) {
    return (
      <div className="relative w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Waiting for location data...</p>
        </div>
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="relative w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center text-red-600">
          <p>Google Maps API key not configured</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          defaultZoom={13}
          defaultCenter={{ lat: pickup.lat, lng: pickup.lng }}
          mapId="tekstar_navigation_map"
          gestureHandling="greedy"
          disableDefaultUI={false}
          zoomControl={true}
          streetViewControl={false}
          mapTypeControl={false}
          fullscreenControl={false}
          style={{ width: '100%', height: '100%' }}
        >
          <MapContent 
            pickup={pickup} 
            dropoff={dropoff} 
            onRouteReady={handleRouteReady}
          />
        </Map>
      </APIProvider>
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading route...</p>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
          <div className="text-center text-red-600">
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {/* Route info overlay */}
      {routeInfo && !isLoading && (
        <div className="absolute top-4 left-4 right-4">
          <Card className="p-4 bg-white/95 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Navigation className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {routeInfo.duration} min • {routeInfo.distance} km
                  </p>
                  {currentStep && (
                    <p className="text-sm text-gray-600">
                      {currentStep.instruction || 'Follow the route'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Location markers legend */}
      <div className="absolute bottom-4 left-4">
        <Card className="p-3 bg-white/95 shadow-lg">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-700">Pickup</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-700">Drop-off</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default NavigationMap;
